import { notifications } from '@mantine/notifications';
import type { InboundMessage } from 'ably';
import type {
  SessionInfoPayload,
  SessionEndedPayload,
  SessionStartedAtPayload,
  CancelSessionReceivedPayload,
  InitSessionConfigStep,
  ExposureConfigFromColumn,
} from '@types';
import { resolveExposureItemFromPayload } from './exposureMultiItem';
import {
  getQuestionStepEntries,
  mapPrimitiveValuesToSessionAnswers,
} from './mapPrimitiveSessionAnswersWithConfig';
import { normalizeSessionEndedAtForPersist } from './sessionDisplayDates';
import type { DeviceConfigurationApi } from '@model/DeviceConfiguration';
import { buildCompletedSessionConfigJson } from '@model/DeviceConfiguration';
import {
  filterValidSessionMessages,
  mapMessagesToFormatted,
} from './sessionEventHandlers';

import {
  extractQuestionAnswerFromSessionInfo,
  type SessionAnswer,
} from './sessionAnswers';
import { useBaselineCalculationStore } from '@store/useBaselineCalculationStore';
/**
 * Processa l'array messages da sessionEnded:
 * - Rimuove 'initial', 'skip', 'done' (case-insensitive)
 * - L'ultimo elemento è il timestamp ended_at
 * - Gli elementi rimanenti (tranne l'ultimo) sono le risposte
 */
export const processSessionEndedMessages = (
  messages: string[]
): {
  answers: string[];
  ended_at: string;
} => {
  if (!Array.isArray(messages) || messages.length === 0) {
    return { answers: [], ended_at: new Date().toISOString() };
  }

  const filtered = messages.filter(
    msg =>
      typeof msg === 'string' &&
      !['initial', 'skip', 'done'].includes(msg.toLowerCase().trim())
  );

  if (filtered.length === 0) {
    return { answers: [], ended_at: new Date().toISOString() };
  }

  const ended_at = filtered[filtered.length - 1];
  const answers = filtered.slice(0, -1);

  return { answers, ended_at };
};
import { useHeartRateStore } from '../store/useHeartRateStore';
import { normalizeEvents } from '../store/useHeartRateStore';

/**
 * Estrae il nome del messaggio da un InboundMessage
 */
export const extractMessageName = (message: InboundMessage): string => {
  const messageData = message.data as unknown;
  const messageName =
    (typeof messageData === 'object' &&
    messageData !== null &&
    'name' in messageData &&
    typeof (messageData as { name: unknown }).name === 'string'
      ? (messageData as { name: string }).name
      : null) ||
    message.name ||
    '';
  return messageName;
};

/**
 * Crea un filter function per i messaggi di sessione.
 * deviceCode opzionale: per eventi senza sessionId (es. mealEnded) si accettano solo se payload.clientId === deviceCode.
 * isExposure: mealEnded è solo evento exposure, viene accettato solo se isExposure === true.
 */
export const createSessionMessageFilter = (
  sessionId: string,
  deviceCode?: string,
  isExposure?: boolean
) => {
  return (message: InboundMessage): boolean => {
    const messageName = extractMessageName(message);
    const payload = message.data as unknown;

    if (typeof payload !== 'object' || payload === null) {
      return false;
    }

    const payloadObj = payload as Record<string, unknown>;

    if (messageName === 'sessionInfo') {
      return (
        typeof payloadObj.deviceCode === 'string' &&
        typeof payloadObj.sessionId === 'string' &&
        payloadObj.sessionId === sessionId
      );
    }

    if (messageName === 'sessionEnded') {
      return (
        typeof payloadObj.deviceCode === 'string' &&
        typeof payloadObj.sessionId === 'string' &&
        payloadObj.sessionId === sessionId
      );
    }

    if (messageName === 'sessionStartedAt') {
      const hasTimestamp =
        typeof payloadObj.timestamp === 'number' ||
        (typeof payloadObj.timestamp === 'string' &&
          !Number.isNaN(Date.parse(payloadObj.timestamp as string)));
      if (!hasTimestamp) return false;
      if (typeof payloadObj.sessionId === 'string') {
        return payloadObj.sessionId === sessionId;
      }
      return true;
    }

    if (messageName === 'cancelSession') {
      return (
        typeof payloadObj.code === 'string' && payloadObj.command === 'cancel'
      );
    }

    if (messageName === 'tutorialCompleted') {
      const hasTimestamp =
        typeof payloadObj.timestamp === 'number' ||
        (typeof payloadObj.timestamp === 'string' &&
          !Number.isNaN(Date.parse(payloadObj.timestamp as string)));
      if (!hasTimestamp) return false;
      if (typeof payloadObj.sessionId === 'string') {
        return payloadObj.sessionId === sessionId;
      }
      return true;
    }

    if (messageName === 'itemShown' || messageName === 'foodShown') {
      const hasTimestamp =
        typeof payloadObj.timestamp === 'number' ||
        (typeof payloadObj.timestamp === 'string' &&
          !Number.isNaN(Date.parse(payloadObj.timestamp as string)));
      const hasItemIds =
        typeof payloadObj.itemIds === 'number' ||
        typeof payloadObj.foodIds === 'number';
      if (!hasTimestamp || !hasItemIds) return false;
      if (typeof payloadObj.sessionId === 'string') {
        return payloadObj.sessionId === sessionId;
      }
      return true;
    }

    if (messageName === 'itemFeedback' || messageName === 'foodFeedback') {
      const hasTimestamp =
        typeof payloadObj.timestamp === 'number' ||
        (typeof payloadObj.timestamp === 'string' &&
          !Number.isNaN(Date.parse(payloadObj.timestamp as string)));
      const hasItemIds =
        typeof payloadObj.itemIds === 'number' ||
        typeof payloadObj.foodIds === 'number';
      const hasScore = typeof payloadObj.score === 'number';
      if (!hasTimestamp || !hasScore || !hasItemIds) return false;
      if (typeof payloadObj.sessionId === 'string') {
        return payloadObj.sessionId === sessionId;
      }
      return true;
    }

    if (messageName === 'sessionEndedAt') {
      const hasTimestamp =
        typeof payloadObj.timestamp === 'number' ||
        (typeof payloadObj.timestamp === 'string' &&
          !Number.isNaN(Date.parse(payloadObj.timestamp as string)));
      if (!hasTimestamp) return false;
      if (typeof payloadObj.sessionId === 'string') {
        return payloadObj.sessionId === sessionId;
      }
      return true;
    }

    if (messageName === 'mealEnded') {
      if (!isExposure) return false;
      const hasTimestamp =
        typeof payloadObj.timestamp === 'number' ||
        (typeof payloadObj.timestamp === 'string' &&
          !Number.isNaN(Date.parse(payloadObj.timestamp as string)));
      if (!hasTimestamp) return false;
      if (typeof payloadObj.sessionId === 'string') {
        return payloadObj.sessionId === sessionId;
      }
      if (deviceCode && typeof payloadObj.clientId === 'string') {
        return payloadObj.clientId === deviceCode;
      }
      return true;
    }

    return false;
  };
};

/**
 * Context per handleSessionInfo
 */
interface SessionInfoContext {
  session: DeviceConfigurationApi | undefined;
  normalizedConfig: InitSessionConfigStep[];
  updateMutation: {
    mutateAsync: (vars: Partial<DeviceConfigurationApi>) => Promise<void>;
  };
  shownNotifications: Set<string>;
}

/**
 * Handler per eventi sessionInfo
 */
export const handleSessionInfo = (
  payload: SessionInfoPayload,
  sessionId: string,
  context: SessionInfoContext
): void => {
  const { session, normalizedConfig, updateMutation, shownNotifications } =
    context;

  if (payload.sessionId !== sessionId) {
    return;
  }

  const messages = payload.messages || [];
  const validMessages = filterValidSessionMessages(messages);

  if (validMessages.length === 0) {
    return;
  }

  const messagesToSkip = ['session cancelled'];
  const messagesWithoutSkipped = validMessages.filter(
    msg => !messagesToSkip.includes(msg.trim().toLowerCase())
  );

  if (messagesWithoutSkipped.length === 0) {
    return;
  }

  if (normalizedConfig.length > 0 && session) {
    const sessionAnswer = extractQuestionAnswerFromSessionInfo(
      messagesWithoutSkipped,
      normalizedConfig,
      new Date().toISOString()
    );

    if (sessionAnswer) {
      let currentAnswers: SessionAnswer[] = [];
      if (session.answers) {
        try {
          const parsed = JSON.parse(session.answers);
          if (Array.isArray(parsed)) {
            currentAnswers = parsed;
          }
        } catch {
          // Ignora errori di parsing
        }
      }

      const answerExists = currentAnswers.some(
        a =>
          a.stepId === sessionAnswer.stepId && a.answer === sessionAnswer.answer
      );

      if (!answerExists) {
        const updatedAnswers = [...currentAnswers, sessionAnswer];
        updateMutation
          .mutateAsync({
            answers: JSON.stringify(updatedAnswers),
          })
          .catch(() => {
            // Errore gestito silenziosamente
          });
      }
    }
  }

  const mappedMessages = mapMessagesToFormatted(messagesWithoutSkipped);

  mappedMessages.forEach(([originalMessage, formattedMessage]) => {
    const notificationKey =
      originalMessage === 'Utente ha interagito'
        ? `${sessionId}-${originalMessage}-${formattedMessage}`
        : `${sessionId}-${originalMessage}`;

    if (shownNotifications.has(notificationKey)) {
      return;
    }

    shownNotifications.add(notificationKey);

    const notificationId = `session-info-${notificationKey}-${Date.now()}`;

    notifications.show({
      id: notificationId,
      color: 'blue',
      withBorder: true,
      radius: 'lg',
      title: originalMessage,
      message: formattedMessage,
      autoClose: 5000,
    });
  });
};

/**
 * Context per handleSessionEnded
 */
interface SessionEndedContext {
  updateMutation: {
    mutateAsync: (vars: Partial<DeviceConfigurationApi>) => Promise<void>;
  };
  processedEvents: Set<string>;
  /** Config normalizzata della sessione (stessi indici di step usati in sessionInfo). */
  normalizedConfig: InitSessionConfigStep[];
  /** Sessione corrente (per fondere config ed exposure events al completamento). */
  session?: DeviceConfigurationApi | null;
}

/**
 * Handler per eventi sessionEnded
 */
export const handleSessionEnded = (
  payload: SessionEndedPayload,
  sessionId: string,
  context: SessionEndedContext
): void => {
  const { updateMutation, processedEvents, normalizedConfig, session } =
    context;

  if (payload.sessionId !== sessionId) {
    return;
  }

  const eventKey = `${payload.sessionId}-${payload.messages?.join(',') || ''}`;

  if (processedEvents.has(eventKey)) {
    return;
  }

  processedEvents.add(eventKey);

  const { answers, ended_at } = processSessionEndedMessages(
    payload.messages || []
  );

  const events = useHeartRateStore.getState().events;
  const phaseEvents = useHeartRateStore.getState().phaseEvents;
  const exposureEvents = useHeartRateStore.getState().exposureEvents ?? [];

  notifications.show({
    id: 'session-completed',
    color: 'green',
    withBorder: true,
    radius: 'lg',
    title: 'Session completed',
    autoClose: 2000,
    message: 'Session completed successfully',
  });

  const endedAtIso = normalizeSessionEndedAtForPersist(ended_at);
  const endedMs = new Date(endedAtIso).getTime();
  const baseEnded = Number.isNaN(endedMs) ? Date.now() : endedMs;
  const getAnswerTimestamp = (i: number) =>
    new Date(baseEnded + i * 1000).toISOString();

  const questionEntries = getQuestionStepEntries(normalizedConfig);
  const answersJson =
    answers.length > 0 && questionEntries.length > 0
      ? JSON.stringify(
          mapPrimitiveValuesToSessionAnswers(
            answers,
            normalizedConfig,
            getAnswerTimestamp
          )
        )
      : JSON.stringify(answers);
  const baseline = useBaselineCalculationStore.getState().baseline;

  const existingConfig = session?.config ?? {};

  const savedData = {
    status: 'completed' as const,
    ended_at: endedAtIso,
    answers: answersJson,
    hrData: normalizeEvents(events),
    results: phaseEvents,
    baseline,
    exposureEvents: [...exposureEvents],
    config: buildCompletedSessionConfigJson(existingConfig, exposureEvents),
  };

  updateMutation
    .mutateAsync(savedData as unknown as Partial<DeviceConfigurationApi>)
    .catch(() => {
      // Errore gestito silenziosamente
    });
};

/**
 * Context per handleSessionStartedAt
 */
interface SessionStartedAtContext {
  updateMutation: {
    mutateAsync: (vars: Partial<DeviceConfigurationApi>) => Promise<void>;
  };
}

/**
 * Handler per eventi sessionStartedAt
 */
export const handleSessionStartedAt = (
  payload: SessionStartedAtPayload,
  sessionId: string,
  context: SessionStartedAtContext
): void => {
  const { updateMutation } = context;

  if (payload.sessionId !== sessionId) {
    return;
  }

  updateMutation
    .mutateAsync({
      started_at: normalizeSessionEndedAtForPersist(String(payload.timestamp)),
    })
    .catch(() => {
      // Errore gestito silenziosamente
    });
};

/**
 * Context per handleCancelSession
 */
interface CancelSessionContext {
  session: DeviceConfigurationApi | undefined;
  updateMutation: {
    mutateAsync: (vars: Partial<DeviceConfigurationApi>) => Promise<void>;
  };
  clearAllEvents: () => void;
}

/**
 * Converte timestamp ISO string in number (ms)
 */
const parseTimestamp = (ts: unknown): number => {
  if (typeof ts === 'number') return ts;
  if (typeof ts === 'string') {
    const parsed = Date.parse(ts);
    return Number.isNaN(parsed) ? Date.now() : parsed;
  }
  return Date.now();
};

/**
 * Handler per eventi Exposure (tutorialCompleted, itemShown, itemFeedback, sessionStartedAt, sessionEndedAt)
 * Aggiunge l'evento allo store per la visualizzazione in Elenco Stati
 */
export const handleExposureEvent = (
  messageName: string,
  payload: Record<string, unknown>,
  sessionId: string,
  exposureConfig?: ExposureConfigFromColumn | null
): void => {
  if (messageName !== 'mealEnded') {
    const payloadSessionId = payload.sessionId;
    if (
      typeof payloadSessionId === 'string' &&
      payloadSessionId !== sessionId
    ) {
      return;
    }
  }

  const timestamp = parseTimestamp(payload.timestamp);

  const eventMap: Record<string, () => void> = {
    tutorialCompleted: () =>
      useHeartRateStore.getState().addExposureEvent({
        type: 'tutorialCompleted',
        timestamp,
      }),
    itemShown: () => {
      const { itemIds } = resolveExposureItemFromPayload(
        payload,
        exposureConfig
      );
      useHeartRateStore.getState().addExposureEvent({
        type: 'itemShown',
        timestamp,
        itemIds,
      });
    },
    itemFeedback: () => {
      const { itemIds } = resolveExposureItemFromPayload(
        payload,
        exposureConfig
      );
      const score =
        typeof payload.score === 'number'
          ? payload.score
          : Number(payload.score) || 0;
      useHeartRateStore.getState().addExposureEvent({
        type: 'itemFeedback',
        timestamp,
        itemIds,
        score,
      });
    },
    sessionStartedAt: () =>
      useHeartRateStore.getState().addExposureEvent({
        type: 'sessionStartedAt',
        timestamp,
      }),
    sessionEndedAt: () =>
      useHeartRateStore.getState().addExposureEvent({
        type: 'sessionEndedAt',
        timestamp,
      }),
    mealEnded: () =>
      useHeartRateStore.getState().addExposureEvent({
        type: 'mealEnded',
        timestamp,
        clientId:
          typeof payload.clientId === 'string' ? payload.clientId : undefined,
      }),
  };
  // Legacy Ably names
  eventMap.foodShown = eventMap.itemShown;
  eventMap.foodFeedback = eventMap.itemFeedback;

  const handler = eventMap[messageName];
  if (handler) {
    handler();
  }
};

/**
 * Handler per eventi cancelSession
 */
export const handleCancelSession = (
  payload: CancelSessionReceivedPayload,
  _sessionId: string,
  context: CancelSessionContext
): void => {
  const { session, updateMutation, clearAllEvents } = context;

  if (!session) {
    return;
  }

  if (payload.code !== session.deviceCode) {
    return;
  }

  clearAllEvents();

  updateMutation
    .mutateAsync({
      status: 'queued',
      started_at: null,
      ended_at: null,
      baseline: 0,
      hrData: [],
      results: [],
    })
    .catch(() => {
      // Errore gestito silenziosamente
    });
};
